import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, combineLatest, map, tap } from 'rxjs';
import { IDepartment } from 'src/app/setting/models/departement';
import { IEmployeeRole } from 'src/app/employee/employee-roles';
import { DepartementStoreService } from 'src/app/setting/services/departement-store.service';
import { EmployeeRoleStoreService } from '../../storages/employee-role-store.service';
import { Employee, IEmployee, IEmployeeCreateRequest, IEmployeeRequest } from '../../employee';
import { EmployeeEmailValidator } from 'src/app/shared/validators-async/employee-email.validator';
import { EmployeeService } from '../../services/employee.service';
import { MatchValidator } from 'src/app/shared/validators-async/match-validator';
import { translate, TranslocoService } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserService } from 'src/app/authentication/services/user.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { EmployeeStore } from 'src/app/stores/component/employee.store';

@UntilDestroy()
@Component({
    selector: 'app-employee-edit',
    templateUrl: './employee-edit.component.html',
    styleUrls: ['./employee-edit.component.css'],
    standalone: false
})
export class EmployeeEditComponent implements OnInit {

  constructor(
    private departementStore: DepartementStoreService,
    private employeeRoleStore: EmployeeRoleStoreService,
    private router: Router,
    private userService: UserService,
    private appnotificationService: AppNotificationService,
    private employeeStore: EmployeeStore,
    private employeeService: EmployeeService
  ) { }

  public employeeForm: UntypedFormGroup;

  public passwordForm: UntypedFormGroup;

  public active = false;

  departements$ : Observable<IDepartment[]> =
    this.departementStore.departements$;

  employeeRoles$ : Observable<IEmployeeRole[]> =
    this.employeeRoleStore.employeeRoles$;

  employees: IEmployee[] = [];

  @Input() employee: IEmployee;

  // Event is triggered, when a new employee is created to enable other employee tabs
  @Output() newEmployeeEvent = new EventEmitter<boolean>();
  isNew : boolean;

  get passwordMatchError() {
    return (
      this.employeeForm.getError('mismatch') &&
      this.employeeForm.get('confirmPassword')?.touched
    );
  }

  vm$ = combineLatest([
    this.employeeStore.employee$.pipe(
      tap(employee => {
        if(employee) {
          this.employeeForm.patchValue({...employee})
        }
      })
    ),
  ]).pipe(
    map(([
      employee,
    ]) => ({
      employee,
    }))
  );

  ngOnInit(): void {
    if(this.employee == null) {
      this.isNew = true;
      this.initForm();
    } else {
      this.isNew = false;
      this.initForm(this.employee);
      this.removeValidators();
    }

    this.employeeService.listEmployees({ pageNumber: 0, pageSize: 999999 })
      .pipe(untilDestroyed(this))
      .subscribe(res => this.employees = res.data
        .filter(e => e.id !== this.employee?.id)
        .map(e => ({ ...e, fullname: `${e.firstName} ${e.lastName}` })));
  }

  initForm(employee?: IEmployee) {
    this.employeeForm = new UntypedFormGroup({
      firstName: new UntypedFormControl(this.employee?.firstName ?? "", { validators: [Validators.required], updateOn: 'blur'}),
      lastName: new UntypedFormControl(this.employee?.lastName ?? "", { validators: [Validators.required], updateOn: 'blur'}),
      email: new UntypedFormControl(this.employee?.email ?? "", { validators: [Validators.required, Validators.email], updateOn: 'blur'}),
      isHidden: new UntypedFormControl(this.employee?.isHidden ?? false),
      password: new UntypedFormControl(null, { validators: [Validators.required, Validators.minLength(6)], updateOn: 'blur'}),
      confirmPassword: new UntypedFormControl(null, { validators: [Validators.required], updateOn: 'blur'}),
      employeeRole: new UntypedFormControl(this.employee?.employeeRole ?? null),
      department: new UntypedFormControl(this.employee?.department ?? null, { validators: [Validators.required], updateOn: 'blur'}),
      supervisorId: new UntypedFormControl(this.employee?.supervisorId ?? null)
    },
    [MatchValidator('password','confirmPassword')]);


    this.passwordForm = new UntypedFormGroup({
      password: new UntypedFormControl(null, { validators: [Validators.required, Validators.minLength(6)], updateOn: 'blur'}),
      confirmPassword: new UntypedFormControl(null, { validators: [Validators.required], updateOn: 'blur'}),
      userId: new UntypedFormControl(this.employee?.userId)
    },
    [MatchValidator('password','confirmPassword')]);
  }

  removeValidators(){
    const emailControl = this.employeeForm.get('email');
    const passwordControl = this.employeeForm.get('password');
    const confirmPasswordControl = this.employeeForm.get('confirmPassword');
    emailControl.setValidators(null);
    passwordControl.setValidators(null);
    confirmPasswordControl.setValidators(null);
  }

  onSubmit(){
    const formValues = this.employeeForm.value;

    if(!this.isNew) {
      const request : IEmployeeRequest = {
        id: this.employee.id,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        employeeRoleId: formValues.employeeRole?.id,
        departmentId: formValues.department.id,
        supervisorId: formValues.supervisorId ?? null,
        isHidden: formValues.isHidden
      }
      this.employeeStore.updateEmployee(request);
    } else {
        const request : IEmployeeCreateRequest = {
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          email: formValues.email,
          password: formValues.password,
          confirmPassword: formValues.confirmPassword,
          employeeRoleId: formValues.employeeRole?.id,
          departmentId: formValues.department.id,
          supervisorId: formValues.supervisorId ?? null
        }

        this.employeeStore.createEmployee(request);
        this.isNew = false;
      }
  }

  onResetPasswort(){
    const formValues = this.passwordForm.value;

    this.userService.resetUserPassword(formValues.userId, formValues.password)
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.appnotificationService.notification$.next(
        {message: translate('employee.service.resetPasswordSuccess'), style: 'success'}
        );
      this.passwordForm.get('password').reset();
      this.passwordForm.get('confirmPassword').reset();
    })
  }

  public onCancel() {
    if(!this.isNew) {
      this.initForm(this.employee);
      this.router.navigate(['settings/employees/']);
    } else {
      this.initForm();
      this.router.navigate(['settings/employees/']);
    }
  }
}
